import {
  SlotMeta,
  DcaNode,
  CfNode,
  SlotContent,
  DcaSlotTarget,
  ExtendedCfNode,
  GeoNode,
  RetargetingNode,
  RetargetingSlotTarget,
} from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import { isRecord } from './helper';
import { isRetargetingPayload, normalizeSlotOrder } from './retargeting/retargetingRuntime';
import {
  hasNonEmptyMangoPrimaryImageFields,
  hasRecentlyViewedMetaCue,
  hydrateRecentlyViewedMangoPrimaryImages,
} from './recentlyViewedUtils';

export type EffectiveSlotSource = SlotMeta['type'];
export type ResolvedEffectiveSlot = {
  source: EffectiveSlotSource;
  slot: SlotContent;
};

function getRetargetingTarget(match?: RetargetingNode): RetargetingSlotTarget | undefined {
  const order = normalizeSlotOrder(match?.order);
  if (order === null) return undefined;
  const { clickTrack, impressionTrack, impressionCount } = match ?? {};
  return {
    order,
    ...(clickTrack !== undefined && { clickTrack }),
    ...(impressionTrack !== undefined && { impressionTrack }),
    ...(impressionCount !== undefined && { impressionCount }),
  };
}

// Pattern builder — defines grid structure (banner/sixpack combos)
export const buildPatternSlots = (
  bannerFirst: boolean,
  squareFirst: boolean,
  dcaNodes: DcaNode[] = [],
  geoNodes: GeoNode[] = [],
  retargetingNodes: RetargetingNode[] = []
): SlotMeta[] => {
  const row1 = squareFirst ? ['square', 'tall', 'square'] : ['tall', 'square', 'tall'];
  const row2 = squareFirst ? ['tall', 'square', 'tall'] : ['square', 'tall', 'square'];
  const sixpack = [...row1, ...row2];

  // Prepare authored DCA orders for fast lookup
  const dcaOrders = new Set<number>();
  for (let i = 0; i < (dcaNodes?.length || 0); i++) {
    const num = Number(dcaNodes[i]?.order);
    if (!Number.isNaN(num)) dcaOrders.add(num);
  }

  // Prepare authored geo-cf orders for fast lookup
  const geoOrders = new Set<number>();
  for (let i = 0; i < (geoNodes?.length || 0); i++) {
    const num = Number(geoNodes[i]?.order);
    if (!Number.isNaN(num)) geoOrders.add(num);
  }

  const retargetingOrders = new Set<number>();
  for (let i = 0; i < (retargetingNodes?.length || 0); i++) {
    const num = normalizeSlotOrder(retargetingNodes[i]?.order);
    if (num !== null) retargetingOrders.add(num);
  }

  const typeForPosition = (position: number): SlotMeta['type'] => {
    if (dcaOrders.has(position)) return 'dca';
    if (retargetingOrders.has(position)) return 'retargeting';
    if (geoOrders.has(position)) return 'geo-cf';
    return 'cf';
  };

  const mapSixpack = (start: number): SlotMeta[] =>
    sixpack.map((size, i) => {
      const position = start + i;
      return {
        size: size as 'square' | 'tall',
        position,
        type: typeForPosition(position),
      };
    });

  if (bannerFirst) {
    const firstSixStart = 2;
    const secondSixStart = 9;
    return [
      { size: 'banner', position: 1, type: typeForPosition(1) },
      ...mapSixpack(firstSixStart),
      { size: 'banner', position: 8, type: typeForPosition(8) },
      ...mapSixpack(secondSixStart),
    ];
  }

  const secondSixStart = 8;
  return [...mapSixpack(1), { size: 'banner', position: 7, type: typeForPosition(7) }, ...mapSixpack(secondSixStart)];
};

/**
 * Merges CF and DCA data into a slots array.
 * @param slotsMeta - The metadata for the slots.
 * @param dcaNodes - The DCA nodes.
 * @param cfData - The CF data.
 * @param opts - The options.
 *   - geoNodes - The geo nodes.
 *   - isAuthenticated - Whether the user is authenticated.
 * @returns The slots array.
 */
export function mergeCfAndDcaToSlots(
  slotsMeta: SlotMeta[],
  dcaNodes: DcaNode[],
  cfData: CfNode[],
  opts?: { geoNodes?: GeoNode[]; retargetingNodes?: RetargetingNode[]; isAuthenticated?: boolean }
): SlotContent[] | null {
  const len = slotsMeta?.length;
  const result = new Array(len);

  // Build a fast lookup map: order → DCA node
  const dcaByOrder = new Map<number, DcaNode>();
  for (let i = 0; i < dcaNodes.length; i++) {
    const order = dcaNodes[i].order;
    if (order !== undefined && order !== null) {
      const num = Number(order);
      if (!Number.isNaN(num)) dcaByOrder.set(num, dcaNodes[i]);
    }
  }

  // Build a fast lookup set for authored geo-cf orders.
  const geoOrders = new Set<number>();
  const geoNodes = opts?.geoNodes ?? [];
  for (let i = 0; i < geoNodes.length; i++) {
    const num = Number(geoNodes[i]?.order);
    if (!Number.isNaN(num)) geoOrders.add(num);
  }
  const isAuthenticated = opts?.isAuthenticated === true;

  const retargetingByOrder = new Map<number, RetargetingNode>();
  const retargetingNodes = opts?.retargetingNodes ?? [];
  for (let i = 0; i < retargetingNodes.length; i++) {
    const order = normalizeSlotOrder(retargetingNodes[i]?.order);
    if (order !== null) {
      retargetingByOrder.set(order, retargetingNodes[i]);
    }
  }

  // DCA slots consume a CF item as their fallback; if DCA payload is empty/failed the CF content renders.
  for (let i = 0; i < len; i++) {
    const slot = slotsMeta[i];
    const position = typeof slot?.position === 'number' ? slot.position : i;
    const authoredDcaSlot = dcaByOrder.get(position);
    // From SSR: resolvedCFData is `master` copy (region undefined) and region copy for logged-in users.
    const resolvedCFData = cfData[i] ?? null;
    const authoredRetargeting = retargetingByOrder.get(position);

    if (authoredDcaSlot) {
      result[i] = {
        ...slot,
        position,
        type: 'dca',
        payload: null,
        cfFallback: resolvedCFData,
        dcaTarget: {
          order: authoredDcaSlot.order,
          mboxParameter: authoredDcaSlot.mboxParameter,
          merchandisingCategory: authoredDcaSlot.merchandisingCategory,
        },
        retargetingTarget: getRetargetingTarget(authoredRetargeting),
      };
    } else if (authoredRetargeting) {
      result[i] = {
        ...slot,
        position,
        type: 'retargeting',
        payload: null,
        cfFallback: resolvedCFData,
        retargetingTarget: getRetargetingTarget(authoredRetargeting),
      };
    } else if (slot?.type === 'geo-cf' || geoOrders.has(position)) {
      // Geo-cf slot: the resolvedCFData CF item (cfData[i]) is always seeded as the
      // fallback. Logged-in users see the resolvedCFData copy upfront (payload set);
      // logged-out users render a skeleton (payload null + isLoading) until the
      // client UXL region call fills it. Either way the resolvedCFData remains the
      // fallback when no region content resolves.

      result[i] = {
        ...slot,
        position,
        type: 'geo-cf',
        payload: isAuthenticated ? resolvedCFData : null,
        cfFallback: resolvedCFData,
        isLoading: !isAuthenticated,
        geoTarget: { order: position },
      };
    } else {
      result[i] = {
        ...slot,
        position,
        type: 'cf',
        payload: resolvedCFData,
        cfFallback: null,
      };
    }
  }
  return result;
}

/** UXL/Target-shaped payloads: not usable as DCA when not loaded or error (cfFallback should show). */
export function isDcaTargetPayloadFailed(payload: unknown): boolean {
  if (!isRecord(payload)) return true;
  const p = payload;
  if (p['isTargetContentLoaded'] === false) return true;
  if (p['isTargetContentError'] === true) return true;
  if (p['loadCfData'] === true) return true;

  // error case when isMango is true but payload has no assets
  const rawMeta = p['metaData'] ?? p['metadata'];
  const metaData = isRecord(rawMeta) ? rawMeta : undefined;
  if (metaData?.['isMango'] === true) {
    const adData = p['adData'];
    const firstAd = Array.isArray(adData) ? adData[0] : undefined;
    const nestedAssets = isRecord(firstAd) ? firstAd['assets'] : undefined;
    const hasPrimaryImages = isRecord(nestedAssets) && hasNonEmptyMangoPrimaryImageFields(nestedAssets);

    if (!hasPrimaryImages && hasRecentlyViewedMetaCue(metaData)) {
      const hydrated = hydrateRecentlyViewedMangoPrimaryImages(p);
      if (!hydrated) {
        return true;
      }
    } else if (!hasPrimaryImages) {
      return true;
    }
  }
  return false;
}

/**
 * Stored when the HTTP/fetch failed or the response was not an array.
 * Treated as failed by {@link isDcaTargetPayloadFailed} so overlay can fall back to CF after DCA has responded.
 * `fromUxlPageModel: false` indicates this was a fetch was from getDCAdata failure in DTL — should retry.
 */
export const DCA_MBOX_RESPONSE_FAILED: Record<string, unknown> = {
  isTargetContentLoaded: true,
  isTargetContentError: true,
  fromUxlPageModel: false,
};

/**
 * Stored when Alloy returned a successful array response but contained no row for this mbox
 * (i.e. no data AND no error). Treated as "resolved" so {@link getDcaData} will not re-fetch,
 * but overlay will fall back to CF content via the `loadCfData` flag.
 */
export const DCA_MBOX_RESPONSE_EMPTY: Record<string, unknown> = {
  isTargetContentLoaded: true,
  isTargetContentError: false,
  loadCfData: true,
};

/**
 * Resolves a list of mbox response items against a set of expected mbox names.
 * - Items with `isTargetContentError: true` → {@link DCA_MBOX_RESPONSE_FAILED}
 * - Items with valid `uxlResponse` → stored as-is
 * - Expected mbox names missing from the response → {@link DCA_MBOX_RESPONSE_EMPTY}
 *
 * Used by both the initial-load seeding (from useDCADynamicModel's pageModel.mboxData)
 * and the client-side Alloy fetch path ({@link mergeAlloyMboxResponses}).
 */
export function resolveMboxResponses(
  mboxItems: Record<string, unknown>[],
  expectedMboxNames: string[]
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const seen = new Set<string>();

  for (const item of mboxItems) {
    const key = String(item?.['mboxName'] ?? '');
    if (!key) continue;
    seen.add(key);
    // Response from pageModel contains uxlResponse
    const fromUxlPageModel = Boolean((item as Record<string, unknown>)?.['uxlResponse']);
    const resolved = (item as Record<string, unknown>)?.['uxlResponse'] ?? item;

    if (isRecord(resolved) && resolved['isTargetContentError'] === true) {
      // Mark whether error came from pageModel (don't retry) or getDCAdata fetch failure (retry)
      out[key] = { ...DCA_MBOX_RESPONSE_FAILED, fromUxlPageModel };
    } else {
      out[key] = resolved;
    }
  }

  for (const name of expectedMboxNames) {
    if (name && !seen.has(name)) {
      out[name] = { ...DCA_MBOX_RESPONSE_EMPTY };
    }
  }

  return out;
}

/**
 * Normalizes an Alloy `fetchDCADynamicModelForAlloy` outcome into per-mbox store entries.
 * Requested mboxes missing from a successful array response receive {@link DCA_MBOX_RESPONSE_EMPTY}
 * so slots can fall back to CF without staying blank (partial / empty success).
 */
export function mergeAlloyMboxResponses(
  payloadMboxes: { mboxName?: string }[],
  response: unknown
): Record<string, unknown> {
  const requestedNames = payloadMboxes.map(item => String(item?.mboxName ?? '')).filter((n): n is string => Boolean(n));

  if (!Array.isArray(response)) {
    const out: Record<string, unknown> = {};
    for (const name of requestedNames) {
      out[name] = { ...DCA_MBOX_RESPONSE_FAILED };
    }
    return out;
  }

  return resolveMboxResponses(response as Record<string, unknown>[], requestedNames);
}

function getDcaFallbackTarget(slot: SlotContent, match?: DcaNode): DcaSlotTarget | undefined {
  const order = match?.order ?? slot.dcaTarget?.order;
  const mboxParameter = match?.mboxParameter ?? slot.dcaTarget?.mboxParameter;
  if (order === undefined && !mboxParameter) return undefined;
  return { order, mboxParameter };
}

function applyCfFallback(slot: SlotContent, match?: DcaNode): SlotContent {
  return {
    ...slot,
    payload: slot.cfFallback,
    type: 'cf',
    cfFallback: slot.cfFallback,
    dcaFallbackTarget: getDcaFallbackTarget(slot, match),
  };
}

function buildCfResolvedSlot(slot: SlotContent): SlotContent {
  return {
    ...slot,
    type: 'cf',
    payload: slot.cfFallback ?? slot.payload ?? null,
  };
}

function buildRetargetingResolvedSlot(slot: SlotContent, retargetingPayload: unknown): SlotContent {
  return {
    ...slot,
    type: 'retargeting',
    payload: retargetingPayload,
  };
}

export function resolveEffectiveSlotSource(
  baseSlot: SlotContent,
  retargetingPayload: unknown | null
): ResolvedEffectiveSlot {
  const slot = { ...baseSlot };
  const hasRetargetingTarget = typeof slot?.retargetingTarget?.order === 'number';
  const validRetargetingPayload = isRetargetingPayload(retargetingPayload) ? retargetingPayload : null;
  const canUseRetargeting = hasRetargetingTarget && validRetargetingPayload !== null;

  if (canUseRetargeting) {
    return { source: 'retargeting', slot: buildRetargetingResolvedSlot(slot, validRetargetingPayload) };
  }

  return { source: 'cf', slot: buildCfResolvedSlot(slot) };
}

// Overlay map onto slots (returns new slots array)
export function overlaySlotsWithDcaRes(
  slots: SlotContent[] = [],
  dcaNodes: DcaNode[] = [],
  opts?: { fillFromCFFallbackIfEmpty?: boolean }
) {
  const hasDca = Array.isArray(dcaNodes) && dcaNodes.length > 0;

  if (!hasDca && opts?.fillFromCFFallbackIfEmpty) {
    const filled = (slots || []).map(s => {
      if (s?.type === 'dca' && s?.cfFallback) {
        return applyCfFallback(s);
      }
      return s;
    });
    return filled;
  }

  const overlaid = (slots || []).map(s => {
    const match = dcaNodes.find(n => n.order === (s as SlotContent)?.position);
    if (match) {
      const payload = match.payload;
      if (isDcaTargetPayloadFailed(payload)) {
        const slot = s as SlotContent;
        if (slot?.cfFallback) {
          return applyCfFallback(slot, match);
        }
        return { ...s, payload: null, type: 'dca' };
      }
      return { ...s, payload, type: 'dca' };
    }
    if (opts?.fillFromCFFallbackIfEmpty && s?.type === 'dca' && s?.cfFallback) {
      return applyCfFallback(s as SlotContent);
    }
    return s;
  });
  return overlaid;
}

/** True when a geo region CF payload is unusable (absent or not a CF-shaped node). */
export function isGeoPayloadUsable(payload: unknown): boolean {
  if (!isRecord(payload)) return false;
  // A valid CF node carries a non-empty `cardType`; region misses fall back to master.
  return typeof payload['cardType'] === 'string' && (payload['cardType'] as string).length > 0;
}

/**
 * Overlay region-specific CF content onto geo-cf slots (client-side).
 *
 * `geoNodes` is a list of `{ order, payload }` where `payload` is the region CF
 * node resolved from the client UXL call (`variation` input). For each geo-cf slot:
 * - usable region payload → render it, clear the loading skeleton.
 * - no/failed region payload → fall back to the seeded `master` (`cfFallback`),
 *   clear the loading skeleton (DTL behaves as if geo-targeting is off).
 *
 * `settled: true` means every reserved geo slot has had its chance to resolve, so
 * any still-pending slot should drop the skeleton and show the master fallback.
 */
export function overlaySlotsWithGeoRes(
  slots: SlotContent[] = [],
  geoNodes: GeoNode[] = [],
  opts?: { settled?: boolean }
): SlotContent[] {
  const byOrder = new Map<number, GeoNode>();
  for (const n of geoNodes) {
    const order = Number(n?.order);
    if (!Number.isNaN(order)) byOrder.set(order, n);
  }

  return (slots || []).map(s => {
    if (s?.type !== 'geo-cf') return s;
    const match = byOrder.get(Number(s?.position));
    if (match && isGeoPayloadUsable(match.payload)) {
      // Region content resolved — render it as a normal (cf-shaped) slot.
      return { ...s, payload: match.payload, isLoading: false };
    }
    // No usable region content. Once settled (or when a match resolved to empty),
    // drop the skeleton and show the seeded master fallback.
    if (match || opts?.settled) {
      return { ...s, payload: s.cfFallback ?? s.payload ?? null, isLoading: false };
    }
    // Still waiting for this slot's region content — keep the skeleton.
    return s;
  });
}

// Normalizes raw AEM GraphQL items into the CF structure.
export function normalizeCfItems(nodes: ExtendedCfNode[], tabName: string) {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];

  const count = nodes.length;
  const result: CfNode[] = new Array(count);

  for (let i = 0; i < count; i++) {
    const item = (nodes[i] ?? {}) as ExtendedCfNode;
    const icon = item.icon;
    const badgeIcon = item.badgeIcon;
    const brandLogoIcon = item.brandLogoIcon;

    const baseCard = {
      ...item,
      id: item.id || `${tabName}-${Math.random().toString(36).slice(2)}`,
      tabName,
      theme: item.theme,
      icon: icon && icon.length ? icon[0] : '',
      description: (item.description as { plaintext?: string })?.plaintext ?? '',
    };

    switch (item.cardType) {
      case 'ProductDetailsCardModel': {
        const tags = item.tagsList;
        let tagListItems: { tagLabel: string }[] = [];
        if (tags && tags.length > 0) {
          const tCount = tags.length;
          tagListItems = new Array(tCount);
          for (let t = 0; t < tCount; t++) {
            tagListItems[t] = { tagLabel: tags[t] };
          }
        }
        result[i] = {
          ...baseCard,
          badgeIcon: badgeIcon && badgeIcon.length ? badgeIcon[0] : '',
          brandLogoIcon: brandLogoIcon && brandLogoIcon.length ? brandLogoIcon[0] : '',
          tagListItems,
        } as CfNode;
        break;
      }
      case 'EnrollmentCardModel':
        result[i] = {
          ...baseCard,
        } as CfNode;
        break;
      case 'CoBrandCardModel':
      case 'LayeredFeatureCardModel':
      default:
        result[i] = baseCard;
        break;
    }
  }

  return result;
}
